import React, { createContext, useContext, useState, useEffect } from 'react';

// Taux de change approximatifs (base CAD)
const EXCHANGE_RATES = {
  CAD: 1,
  EUR: 0.68,
  USD: 0.74,
  XOF: 445.50, // Franc CFA
};

const CURRENCY_SYMBOLS = {
  CAD: 'CAD',
  EUR: '€',
  USD: '$',
  XOF: 'FCFA',
};

const CURRENCY_NAMES = {
  CAD: 'Dollar canadien (CAD)',
  EUR: 'Euro (EUR)',
  USD: 'Dollar US (USD)',
  XOF: 'Franc CFA (XOF)',
};

const CurrencyContext = createContext(null);

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState(() => {
    const saved = localStorage.getItem('kako_currency');
    return saved || 'CAD';
  });

  useEffect(() => {
    localStorage.setItem('kako_currency', currency);
  }, [currency]);

  // Convertit un montant de CAD vers la devise sélectionnée
  const convertFromCAD = (amountInCAD) => {
    if (!amountInCAD || isNaN(amountInCAD)) return 0;
    const rate = EXCHANGE_RATES[currency] || 1;
    return amountInCAD * rate;
  };

  // Convertit un montant de la devise sélectionnée vers CAD
  const convertToCAD = (amount) => {
    if (!amount || isNaN(amount)) return 0;
    const rate = EXCHANGE_RATES[currency] || 1;
    return amount / rate;
  };

  // Formate un montant avec le symbole de devise
  const formatPrice = (amountInCAD, options = {}) => {
    const { showOriginal = false, decimals = 2 } = options;
    const converted = convertFromCAD(amountInCAD);
    const symbol = CURRENCY_SYMBOLS[currency];
    
    // Pour XOF, pas de décimales
    const actualDecimals = currency === 'XOF' ? 0 : decimals;
    const formatted = converted.toFixed(actualDecimals);
    
    if (showOriginal && currency !== 'CAD') {
      return `${formatted} ${symbol} (${amountInCAD?.toFixed(2)} CAD)`;
    }
    
    return `${formatted} ${symbol}`;
  };

  // Obtient le symbole de la devise actuelle
  const getSymbol = () => CURRENCY_SYMBOLS[currency];

  // Liste des devises disponibles
  const availableCurrencies = Object.keys(EXCHANGE_RATES);

  return (
    <CurrencyContext.Provider value={{
      currency,
      setCurrency,
      convertFromCAD,
      convertToCAD,
      formatPrice,
      getSymbol,
      availableCurrencies,
      currencyNames: CURRENCY_NAMES,
      exchangeRates: EXCHANGE_RATES,
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyContext;
